import _ from "lodash";
import React, { useState, useRef } from "react";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import { CreateDropDown } from "Attachment";
import { ICONS } from "Constants";
import ModalFooter from "AppComponents/ModalFooter";
import classes from "./CreateDropDownWithAudioRecorder.scss";
import { Button, Modal, Checkbox } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { getSettingValue } from "modules/PermissionModule";
import { updateSettings } from "modules/ConfigurationThunks";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import update from "immutability-helper";
import { updateAttachment } from "OrganizationResources/modules/OrganizationResourcesModule";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const styles = {
  stripContainerStyle: {
    margin: "0px 84px",
  },
  audioContainerStyle: { width: "max-content" },
  footerContainerStyle: {
    border: "none",
    padding: "0px",
  },
  rightContainerStyle: {
    columnGap: "8px",
  },
};

const CreateDropDownWithAudioRecorder = props => {
  const [uploadStatus, setUploadStatus] = useState(null);
  const [updateRecordingStatus, setUpdateRecordingStatus] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleCheck = () => {
    setIsChecked(!isChecked);
  };

  const audioSelectorRef = useRef();

  const {
    commonCreateDropDownProps = {},
    buttonIconType,
    modalConfig,
    mode,
    value,
    fieldKey,
    parentId,
    parentType,
    limit,
    buttonLabelInProgress,
    label = "",
    updateInputFieldLocally,
    testId,
  } = props;

  const { t } = useI18n(["common", "project"]);

  const Icon = ICONS[buttonIconType];

  const { headerLabel, bodyLabel, displayDontShowAgainLabel, button2Label } =
    modalConfig || {};

  const leftElement = displayDontShowAgainLabel && (
    <div className={classes.dontShowAgain}>
      <Checkbox isChecked={isChecked} onClick={handleCheck} testId={testId} />
      <span className="text-label">{t("project:dont_show_message_again")}</span>
    </div>
  );

  const handleAudioAttachment = async params => {
    const { name, updateAttachment, defaultFileName, t, entityDetails } = props;

    const { studentName } = entityDetails;

    const updatedValue = _.get(params, `${name}`, null);

    const fileName = defaultFileName
      ? t(defaultFileName)
      : t("project:oral_label", {
          label: studentName,
        });

    const attachmentId = _.get(updatedValue, "id", "");
    if (attachmentId) {
      await updateAttachment({
        id: attachmentId,
        name: fileName,
      });
    }

    const updatedAttachmentValue = update(updatedValue, {
      ["name"]: {
        $set: fileName,
      },
    });

    params[name] = !_.isEmpty(updatedAttachmentValue)
      ? [updatedAttachmentValue]
      : [];

    updateInputFieldLocally(params);
  };

  const onDescriptionModalClickOkay = () => {
    const { modalConfig, updateSettings } = props;

    const userSettingName = _.get(modalConfig, "userSettingName", "");
    updateSettings({
      settings: [
        {
          name: userSettingName,
          value: "true",
        },
      ],
      showToastMsg: true,
    });
  };

  const checkSettingValue = () => {
    const { modalConfig, userId, userEntityType } = props;

    const userSettingName = _.get(modalConfig, "userSettingName", "");

    const settingValue = getSettingValue({
      name: userSettingName,
      userId,
      userEntityType,
    });

    return settingValue;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsChecked(false);
    audioSelectorRef.current = null;
  };

  const handleClickOkay = () => {
    audioSelectorRef?.current?.click();
  };

  const isAudioButtonDisabled = uploadStatus || updateRecordingStatus;

  return (
    <>
      <CreateDropDown
        buttonComponent={
          <Button
            _legacy_variant="outlined-subtle"
            type="outlined"
            variant="neutral"
            disabled={isAudioButtonDisabled}
          >
            <AudioRecorderSelector
              mode={mode}
              value={value ? value : []}
              name={fieldKey}
              fieldId={parentId}
              parentType={parentType}
              buttonComponent={
                <span
                  className={classes.audioButton}
                  onClick={e => {
                    if (!audioSelectorRef.current) {
                      if (!checkSettingValue()) {
                        audioSelectorRef.current = e.currentTarget;
                        e.stopPropagation();
                        setShowModal(true);
                      }
                    } else {
                      if (isChecked) {
                        onDescriptionModalClickOkay();
                      }
                      handleCloseModal();
                    }
                  }}
                >
                  <Icon disabled={isAudioButtonDisabled} />
                  <span>
                    {updateRecordingStatus ? buttonLabelInProgress : label}
                  </span>
                </span>
              }
              updateInputField={handleAudioAttachment}
              containerStyle={styles.audioContainerStyle}
              uploadStatus={value => setUploadStatus(value)}
              updateRecordingStatus={value => setUpdateRecordingStatus(value)}
              showValue={false}
              stripContainerStyles={styles.stripContainerStyle}
              limit={limit}
              version={"v4"}
              saveDraftRecording={false}
              title={t("project:audio_recording")}
              testId={testId}
            />
          </Button>
        }
        disabled={isAudioButtonDisabled}
        isSplitDropdown={true}
        {...commonCreateDropDownProps}
        testId={testId}
      />
      {showModal && (
        <Modal width="600px" height="312px" onClose={handleCloseModal}>
          <Modal.Header title={t(headerLabel)} testId={testId} />
          <Modal.Body>
            <div>
              {bodyLabel ? (
                t(bodyLabel)
              ) : (
                <div className={classes.modalBodyText}>
                  <span>{t("project:recording_body_label")}</span>
                  <span>{t("project:recording_warning_label")}</span>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer hasSeparator>
            <ModalFooter
              buttonLabel1={t("common:cancel")}
              buttonLabel2={button2Label ? t(button2Label) : t("common:okay")}
              onClickButton1={handleCloseModal}
              onClickButton2={handleClickOkay}
              containerStyle={styles.footerContainerStyle}
              leftElement={leftElement}
              testId={testId}
              dsVersion="2.0"
              button1Variant="neutral"
              button1Type="outlined"
              button2Variant="primary"
              button2Type="fill"
              rightContainerStyle={styles.rightContainerStyle}
            />
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();

  return {
    userId: _.get(userInfo, "id", ""),
    userEntityType: _.get(userInfo, "userEntityType", ""),
  };
};

const mapActionCreators = {
  updateSettings,
  updateAttachment,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  CreateDropDownWithAudioRecorder
);
